import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { Pane } from "src/app/components/Pane/Pane";
import { useShowPane } from "src/hooks/useShowPane";
import { useQuery } from "src/app/methods/useQuery";

import { useSelector } from "react-redux";
import { RootState } from "src/redux/reducers";

import { Gallery } from "src/app/components/Gallery/Gallery";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { API_URLS } from "src/utils/API_URLS";
import { CustomSelectOption, GalleryConfig, SOCIAL_ICONS } from "src/types";
import { IDDateRangeOption } from "../../InsightsDashboard";
import { GallerySortByKey } from "src/app/components/Gallery/components/GallerySortBy/utils";
import { getFiltersFromSetup } from "../../utils";
import axios from "axios";
import {
  Breadcrumbs,
  BreadcrumbsItem,
} from "src/app/components/Breadcrumbs/Breadcrumbs";
import { showErrorToast } from "src/utils/methods";
import { getRoute, ROUTES } from "src/utils/ROUTES";
import { Img } from "src/app/components/Img/Img";
import placeholder from "src/images/placeholder-image.svg";
import creatorPlaceholder from "src/images/creator-placeholder.svg";

import "./IDGalleryPane.scss";
import Flag from "src/app/components/Flag/Flag";

export interface PostsData {
  url: string;
  engagementRate: number;
  publishedDate: string;
  socialProvider: string;
  likes: number;
  views: number | null;
  comments: number;
  description: string;
  thumbUrl: string;
  imageUrl: string;
}

interface ItemData {
  breadcrumbText: string;
  titleText: string;
  icon: string | null;
  imgUrl: string | null;
}

interface Props {
  hidePaneFromOutside?: () => void;
  dateFrom: string | null;
  dateTo: string | null;
  dateRange: IDDateRangeOption | null;
}

export const IDGalleryPane = (props: Props) => {
  const { hidePaneFromOutside, dateFrom, dateTo, dateRange } = props;

  const [itemData, setItemData] = useState<ItemData | null>(null);

  const { activeWorkspaceUuid: wsWorkspaceUuid } = useSelector(
    (state: RootState) => state.mainReducer,
  );
  const { setup, setupConfig } = useSelector(
    (state: RootState) => state.insightsDashboardReducer,
  );

  const filters = getFiltersFromSetup(setup, setupConfig);

  const history = useHistory();
  const query = useQuery();
  const { pathname, search } = useLocation();

  const type = query.get("type");
  const value = query.get("value");

  const hidePaneFunction = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("type");
    url.searchParams.delete("value");
    history.push(url.pathname + url.search);
  };

  const [showPane, hidePane] = useShowPane(() => {
    if (hidePaneFromOutside) {
      hidePaneFromOutside();
    } else {
      hidePaneFunction();
    }
  });

  const getItemData = useCallback(async () => {
    try {
      const {
        data: { content },
      } = await axios.post(API_URLS.getSocialInsightsGalleryItemData, {
        value,
        type,
      });

      setItemData(content);
    } catch (err) {
      showErrorToast();
    }
  }, [type, value]);

  useEffect(() => {
    getItemData();
  }, [getItemData]);

  const getConfig: () => GalleryConfig = () => {
    return {
      infiniteScroll: {
        url: API_URLS.getSocialInsightsPosts,
        socialProfiles: [],
        additionalFilters: {
          type,
          value,
          dateFrom,
          dateTo,
          dateRange: dateRange?.value,
          ...filters,
          countries: filters.countries.map((country) => country.value),
          workspacePublicationsType: setup.workspacePublicationsType,
          hashtagsCriteria: setup.hashtagsCriteria?.value ?? "or",
          mentionsCriteria: setup.mentionsCriteria?.value ?? "or",
          hashtagsMentionsCriteria:
            setup.hashtagsMentionsCriteria?.value ?? "or",
          wsWorkspaceUuid,
        },
        limit: 24,
      },
    };
  };

  const normalizeKeyword = (keyword: string, prefix: "#" | "@") => {
    const trimmed = keyword?.trim();

    if (!trimmed) {
      return null;
    }

    if (trimmed.startsWith(prefix)) {
      return trimmed;
    }

    return `${prefix}${trimmed.replace(/^[@#]+/, "")}`;
  };

  const galleryKeywords = [
    ...(filters?.hashtags || [])
      .map((keyword) => normalizeKeyword(keyword, "#"))
      .filter((keyword): keyword is string => Boolean(keyword)),
    ...(filters?.mentions || [])
      .map((keyword) => normalizeKeyword(keyword, "@"))
      .filter((keyword): keyword is string => Boolean(keyword)),
  ];

  const uniqueGalleryKeywords = Array.from(new Set(galleryKeywords));

  const hasKeywordsToHighlight = uniqueGalleryKeywords.length > 0;

  const getInitialSortBy: () =>
    | CustomSelectOption<GallerySortByKey>
    | undefined = () => {
    if (type !== "all") return undefined;

    if (value === "er") {
      return { value: "er", label: <span data-qa="sort-by-er">ER</span> };
    }

    return {
      value: "publishedDate",
      label: <IDHFormattedMessage id="ws_date" defaultMessage="Date" />,
    };
  };

  const getPlaceholder = () => {
    if (type === "influencer") return creatorPlaceholder;

    return placeholder;
  };

  console.log(itemData, type);

  const renderImage = () => {
    if (!itemData?.imgUrl && !itemData?.icon) return null;

    if (type === "country" && itemData?.icon) {
      // @ts-ignore
      return <Flag countryCode={itemData?.icon} />;
    }

    if (type === "platform" && itemData?.icon) {
      return (
        <img
          className="id-gallery-pane__provider"
          src={SOCIAL_ICONS[itemData?.icon]}
          alt={itemData?.icon}
        />
      );
    }

    return (
      <Img
        className="id-gallery-pane__image"
        src={itemData?.imgUrl || undefined}
        fallbackImageSrc={getPlaceholder()}
      />
    );
  };

  return (
    <Pane
      className="id-gallery-pane"
      backdropClassName="id-gallery-pane__backdrop"
      showPane={showPane}
      hidePane={hidePane}
    >
      <div className="id-gallery-pane__topbar">
        <Breadcrumbs>
          <BreadcrumbsItem
            text={
              <IDHFormattedMessage
                id="ws_social_insights_dashboard"
                defaultMessage="Social Insights Dashboard"
              />
            }
            to={getRoute(ROUTES.SOCIAL_INSIGHTS, { wsWorkspaceUuid })}
          />
          {itemData?.breadcrumbText && (
            <BreadcrumbsItem
              text={itemData?.breadcrumbText}
              to={`${pathname}${search}`}
            />
          )}
        </Breadcrumbs>
      </div>
      <div className="id-gallery-pane__content">
        <Gallery
          config={getConfig()}
          header={{
            title: (
              <span className="id-gallery-pane__header">
                {renderImage()}
                {itemData?.titleText}
              </span>
            ),
            keywords: hasKeywordsToHighlight
              ? uniqueGalleryKeywords
              : undefined,
          }}
          creatorData={{
            username: "unknown",
            profilePictureUrl: undefined,
          }}
          initialSortBy={getInitialSortBy()}
          enableSortBy
          showMore
        />
      </div>
    </Pane>
  );
};
